import { fromZaLocalToUtcIso, toZaDateKey, toZaNowDateKey } from '@/lib/datetime'

/**
 * A follow-up is a reminder to get back to a customer. It is a *ping*, not a
 * container: the narrative lives in the customer's notes thread.
 *
 * The scheduling model is one-shot at heart — `dueAt` is the single effective
 * due date, and every action just moves it:
 *
 *   snooze → pushes `dueAt` out, leaving the ladder alone
 *   done   → advances the ladder and re-arms `dueAt` from the next step
 *   close  → the only way to end a follow-up for good
 *
 * Done re-arms rather than ends, because the failure this feature exists to
 * prevent is a customer quietly falling off the radar.
 */

export type FollowUpStatus = 'open' | 'closed'

export type IntervalUnit = 'day' | 'week' | 'month' | 'year'

/** An interval rung of the reminder ladder, e.g. `{ amount: 6, unit: 'month' }`. */
export interface IntervalStep {
  amount: number
  unit: IntervalUnit
}

/**
 * A rung pinned to a calendar day (ZA-local date key, `YYYY-MM-DD`) — "then on
 * 15 December". A date can only come round once, so a ladder that ends on a
 * date rung ends: there is nothing for the last rung to repeat.
 */
export interface DateStep {
  onDate: string
}

export type ScheduleStep = IntervalStep | DateStep

export function isDateStep(step: ScheduleStep): step is DateStep {
  return 'onDate' in step
}

export type FollowUpEventType =
  | 'created'
  | 'snoozed'
  | 'done'
  | 'rescheduled'
  | 'closed'
  | 'reopened'
  | 'assigned'

export interface FollowUpEvent {
  type: FollowUpEventType
  /** UTC ISO timestamp of the action. */
  at: string
  /** User id who performed it. */
  by: string
  /** The due date the follow-up carried *after* the action. */
  dueAt?: string
  /** Free text captured with the action, echoed into the customer thread. */
  note?: string
  /** Who the follow-up was handed to (assigned events only). */
  assigneeId?: string
}

/** Which service this follow-up is about, if any. Mirrors archived_services. */
export type FollowUpServiceType = 'callout' | 'remote' | 'sitdown' | 'job'

export interface FollowUpServiceRef {
  type: FollowUpServiceType
  id: string
}

export interface FollowUp {
  id: string
  customerId: string
  title: string
  details?: string
  service?: FollowUpServiceRef
  /** UTC ISO. The single source of truth for when this next surfaces. */
  dueAt: string
  /** User-built ladder of intervals. Empty means a pure one-shot. */
  schedule: ScheduleStep[]
  /** Index of the step that will be used on the next `done`. */
  scheduleIndex: number
  status: FollowUpStatus
  lastDoneAt?: string
  history: FollowUpEvent[]
  /** User id of whoever is chasing this, when it has an owner. */
  assigneeId?: string
  /**
   * Id of a follow_up_categories record — a curated list managed in
   * PocketBase, never free text, so the table can sort and filter on it.
   */
  categoryId?: string
  createdBy: string
  closedAt?: string
  closedBy?: string
  createdAt: string
  updatedAt: string
}

export type NewFollowUpInput = Pick<FollowUp, 'customerId' | 'title' | 'dueAt'> &
  Partial<
    Pick<FollowUp, 'details' | 'service' | 'schedule' | 'assigneeId' | 'categoryId'>
  >

/** A follow_up_categories record: the curated category list. */
export interface FollowUpCategory {
  id: string
  name: string
  sortOrder: number
}

/** What the UI needs to sort and group the inbox. */
export type DueState = 'overdue' | 'today' | 'upcoming'

const DAY_MS = 24 * 60 * 60 * 1000

function clampDayOfMonth(year: number, monthIndex: number, day: number) {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
  return Math.min(day, lastDay)
}

/**
 * Add an interval to a UTC ISO timestamp.
 *
 * Month and year steps land on the same day-of-month, clamped to the end of
 * shorter months — "remind me in a month" from 31 January means 28 February,
 * not 3 March.
 */
export function addInterval(iso: string, step: IntervalStep): string {
  const from = new Date(iso)
  if (Number.isNaN(from.getTime())) return iso

  const amount = Math.trunc(step.amount)

  if (step.unit === 'day' || step.unit === 'week') {
    const days = step.unit === 'week' ? amount * 7 : amount
    return new Date(from.getTime() + days * DAY_MS).toISOString()
  }

  const months = step.unit === 'year' ? amount * 12 : amount
  const targetMonth = from.getUTCMonth() + months
  const targetYear = from.getUTCFullYear() + Math.floor(targetMonth / 12)
  const monthIndex = ((targetMonth % 12) + 12) % 12

  return new Date(
    Date.UTC(
      targetYear,
      monthIndex,
      clampDayOfMonth(targetYear, monthIndex, from.getUTCDate()),
      from.getUTCHours(),
      from.getUTCMinutes(),
      from.getUTCSeconds(),
      from.getUTCMilliseconds(),
    ),
  ).toISOString()
}

/**
 * The step a `done` would re-arm with.
 *
 * Once the ladder is exhausted its last rung repeats forever, so a follow-up
 * with a schedule never falls silent — unless that last rung is a date, which
 * can only come round once, so the ladder ends there. A follow-up with no
 * schedule is a genuine one-shot. In both ending cases this returns undefined
 * and `markDone` closes instead of re-arming.
 */
export function nextScheduleStep(followUp: Pick<FollowUp, 'schedule' | 'scheduleIndex'>): ScheduleStep | undefined {
  const { schedule, scheduleIndex } = followUp
  if (!schedule.length) return undefined
  const step = schedule[scheduleIndex]
  if (step) return step
  const last = schedule[schedule.length - 1]
  return isDateStep(last) ? undefined : last
}

/** The due date a step produces when armed at `fromIso`. */
export function stepDueAt(fromIso: string, step: ScheduleStep): string {
  if (isDateStep(step)) return fromZaLocalToUtcIso(step.onDate, '08:00')
  return addInterval(fromIso, step)
}

/**
 * The rungs still ahead of a follow-up, as an editable ladder.
 *
 * Mid-ladder that is the un-consumed tail. An exhausted interval-terminal
 * ladder is its last rung repeating forever, so that rung is what remains;
 * an exhausted date-terminal ladder has nothing left. Editing UIs show this
 * and save it back with `scheduleIndex` reset to 0, so what the user sees
 * is exactly what will happen next.
 */
export function remainingSchedule(
  followUp: Pick<FollowUp, 'schedule' | 'scheduleIndex'>,
): ScheduleStep[] {
  const { schedule, scheduleIndex } = followUp
  if (scheduleIndex < schedule.length) return schedule.slice(scheduleIndex)
  const last = schedule[schedule.length - 1]
  return last && !isDateStep(last) ? [last] : []
}

/** True when a `done` re-arms rather than closes. Drives the button copy. */
export function doneWillRearm(followUp: Pick<FollowUp, 'schedule' | 'scheduleIndex'>): boolean {
  return nextScheduleStep(followUp) !== undefined
}

export interface ActionContext {
  /** UTC ISO timestamp of when the action happened. */
  at: string
  /** User id performing it. */
  by: string
  note?: string
}

function withEvent(
  followUp: FollowUp,
  event: FollowUpEvent,
  changes: Partial<FollowUp>,
): FollowUp {
  return {
    ...followUp,
    ...changes,
    history: [...followUp.history, event],
    updatedAt: event.at,
  }
}

/**
 * Record that the follow-up was acted on.
 *
 * With a schedule, this advances the ladder and re-arms `dueAt` from the
 * *moment of completion* — a yearly check-in done three weeks late is due a
 * year from today, not a year from the date it slipped past.
 *
 * Without one, there is nothing to re-arm to and the follow-up closes.
 */
export function markDone(followUp: FollowUp, context: ActionContext): FollowUp {
  const step = nextScheduleStep(followUp)

  if (!step) {
    return closeFollowUp({ ...followUp, lastDoneAt: context.at }, context, 'done')
  }

  const dueAt = stepDueAt(context.at, step)

  return withEvent(
    followUp,
    { type: 'done', at: context.at, by: context.by, dueAt, note: context.note },
    {
      dueAt,
      lastDoneAt: context.at,
      scheduleIndex: Math.min(followUp.scheduleIndex + 1, followUp.schedule.length),
      status: 'open',
    },
  )
}

/**
 * Push the follow-up out without touching the ladder — "not now, try me
 * Thursday". Snoozing measures from now, not from the old due date, so
 * snoozing an overdue item by a week means a week from today.
 */
export function snooze(followUp: FollowUp, step: ScheduleStep, context: ActionContext): FollowUp {
  const dueAt = stepDueAt(context.at, step)

  return withEvent(
    followUp,
    { type: 'snoozed', at: context.at, by: context.by, dueAt, note: context.note },
    { dueAt, status: 'open' },
  )
}

/** Move the due date to an explicit date, leaving the ladder alone. */
export function reschedule(followUp: FollowUp, dueAt: string, context: ActionContext): FollowUp {
  return withEvent(
    followUp,
    { type: 'rescheduled', at: context.at, by: context.by, dueAt, note: context.note },
    { dueAt, status: 'open' },
  )
}

/** End the follow-up for good. The only action that stops the pinging. */
export function closeFollowUp(
  followUp: FollowUp,
  context: ActionContext,
  via: 'closed' | 'done' = 'closed',
): FollowUp {
  return withEvent(
    followUp,
    { type: via, at: context.at, by: context.by, note: context.note },
    { status: 'closed', closedAt: context.at, closedBy: context.by },
  )
}

/** Hand the follow-up to someone. Changes who is chasing it, nothing else. */
export function assign(followUp: FollowUp, assigneeId: string, context: ActionContext): FollowUp {
  return withEvent(
    followUp,
    { type: 'assigned', at: context.at, by: context.by, assigneeId },
    { assigneeId },
  )
}

/** Bring a closed follow-up back with a fresh due date. */
export function reopen(followUp: FollowUp, dueAt: string, context: ActionContext): FollowUp {
  return withEvent(
    followUp,
    { type: 'reopened', at: context.at, by: context.by, dueAt, note: context.note },
    { dueAt, status: 'open', closedAt: undefined, closedBy: undefined },
  )
}

/**
 * Whether a follow-up is asking for attention.
 *
 * "Due" is day-granular in South African local time: something due at 09:00
 * counts as due from the start of that day, so staff see the day's work when
 * they open the app rather than watching items trickle in.
 */
export function isDue(followUp: Pick<FollowUp, 'status' | 'dueAt'>, now = new Date()): boolean {
  if (followUp.status !== 'open') return false
  return toZaDateKey(followUp.dueAt) <= toZaNowDateKey(now)
}

export function dueState(followUp: Pick<FollowUp, 'dueAt'>, now = new Date()): DueState {
  const dueKey = toZaDateKey(followUp.dueAt)
  const todayKey = toZaNowDateKey(now)
  if (dueKey < todayKey) return 'overdue'
  if (dueKey === todayKey) return 'today'
  return 'upcoming'
}

/** The nav badge count: open follow-ups that have come due. */
export function countDue(followUps: Pick<FollowUp, 'status' | 'dueAt'>[], now = new Date()): number {
  return followUps.filter((followUp) => isDue(followUp, now)).length
}

// ---- Server-side search (upcoming / archive) --------------------------------
//
// The synced follow-ups collection carries only the *due* slice — the set staff
// work down, which stays small. Everything else grows without bound (a healthy
// shop schedules years of reminders and closes thousands), so the Upcoming and
// Archive tabs page through the server instead, mirroring the archived-services
// and customers searches.

/** Which server-paged slice a search reads. Due-now stays local and synced. */
export type FollowUpSearchScope = 'upcoming' | 'archive'

export const followUpSearchSortKeys = ['due', 'title', 'created'] as const

export type FollowUpSearchSortKey = (typeof followUpSearchSortKeys)[number]

export interface FollowUpsSearchFilters {
  /** Substring match on the customer's full name. */
  customer?: string
  /** Exact match on the assignee's user id. */
  assigneeId?: string
  /** Exact match on the category id. */
  categoryId?: string
  /** Substring match on the follow-up title. */
  title?: string
}

export interface FollowUpsSearchInput {
  scope: FollowUpSearchScope
  filters?: FollowUpsSearchFilters
  sort?: { key: FollowUpSearchSortKey; dir: 'asc' | 'desc' }
  page: number
  pageSize: number
}

/**
 * Search results come back as ordered keys: the matching follow-ups are
 * upserted into the adapter's follow-ups collection, so the UI reads records
 * through TanStack DB and uses the keys only for membership + order.
 */
export interface FollowUpsSearchResult {
  keys: string[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

/** A materialised page of follow-ups, before being upserted + keyed. */
export interface FollowUpsSearchPage {
  items: FollowUp[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

function zaDateKeyPlusDays(now: Date, days: number): string {
  return toZaDateKey(new Date(now.getTime() + days * DAY_MS).toISOString())
}

/**
 * The UTC instant where "upcoming" starts: midnight tonight, ZA-local.
 * Due-ness is day-granular (see {@link isDue}), so anything due from tomorrow
 * onward is upcoming and anything before this instant has come due.
 */
export function upcomingBoundaryUtcIso(now = new Date()): string {
  return fromZaLocalToUtcIso(zaDateKeyPlusDays(now, 1), '00:00')
}

/**
 * How far ahead the synced collection loads: one ZA day past the upcoming
 * boundary. The buffer means a tab left open overnight still finds tomorrow's
 * follow-ups locally when they come due at midnight, without a refetch.
 */
export function dueSyncHorizonUtcIso(now = new Date()): string {
  return fromZaLocalToUtcIso(zaDateKeyPlusDays(now, 2), '00:00')
}

const followUpSortValue: Record<
  FollowUpSearchSortKey,
  (followUp: FollowUp) => string
> = {
  due: (followUp) => followUp.dueAt,
  title: (followUp) => followUp.title.toLowerCase(),
  created: (followUp) => followUp.createdAt,
}

/** Newest-first for the archive, soonest-first for what is still ahead. */
export function defaultFollowUpSort(scope: FollowUpSearchScope): {
  key: FollowUpSearchSortKey
  dir: 'asc' | 'desc'
} {
  return { key: 'due', dir: scope === 'archive' ? 'desc' : 'asc' }
}

/**
 * In-memory equivalent of the PocketBase follow-ups search, so the in-memory
 * adapter (Storybook/tests) behaves like the server: same scope boundaries,
 * filter semantics (`~` substring, `=` exact), sort keys and pagination shape.
 */
export function searchFollowUpRows(
  followUps: FollowUp[],
  getCustomerName: (customerId: string) => string,
  input: FollowUpsSearchInput,
  now = new Date(),
): FollowUpsSearchPage {
  const { filters = {} } = input
  const todayKey = toZaNowDateKey(now)
  const customerQuery = filters.customer?.trim().toLowerCase()
  const titleQuery = filters.title?.trim().toLowerCase()

  const filtered = followUps.filter((followUp) => {
    if (input.scope === 'archive') {
      if (followUp.status !== 'closed') return false
    } else {
      if (followUp.status !== 'open') return false
      if (toZaDateKey(followUp.dueAt) <= todayKey) return false
    }
    if (
      customerQuery &&
      !getCustomerName(followUp.customerId).toLowerCase().includes(customerQuery)
    ) {
      return false
    }
    if (filters.assigneeId && followUp.assigneeId !== filters.assigneeId) {
      return false
    }
    if (filters.categoryId && followUp.categoryId !== filters.categoryId) {
      return false
    }
    if (titleQuery && !followUp.title.toLowerCase().includes(titleQuery)) {
      return false
    }
    return true
  })

  const sort = input.sort ?? defaultFollowUpSort(input.scope)
  const dir = sort.dir === 'asc' ? 1 : -1
  const value = followUpSortValue[sort.key]
  filtered.sort((a, b) => {
    const av = value(a)
    const bv = value(b)
    return av < bv ? -dir : av > bv ? dir : 0
  })

  const pageSize = Math.max(1, input.pageSize)
  const totalItems = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const page = Math.min(Math.max(1, input.page), totalPages)
  const start = (page - 1) * pageSize

  return {
    items: filtered.slice(start, start + pageSize),
    page,
    pageSize,
    totalItems,
    totalPages,
  }
}

const UNIT_LABELS: Record<IntervalUnit, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
}

/** 'in 6 months', 'on 2026-12-15' — the phrase used on buttons and in summaries. */
export function describeStep(step: ScheduleStep): string {
  if (isDateStep(step)) return `on ${step.onDate}`
  const unit = UNIT_LABELS[step.unit]
  if (step.amount === 1) return `in a ${unit}`
  return `in ${step.amount} ${unit}s`
}

/**
 * 'in a year, then every 6 months' — the ladder in one line, spelling out that
 * the last rung repeats. A ladder ending on a date rung ends instead:
 * 'in a week, then on 2026-12-15, then done'.
 */
export function describeSchedule(schedule: ScheduleStep[]): string {
  if (!schedule.length) return 'once'

  const last = schedule[schedule.length - 1]
  if (isDateStep(last)) {
    return `${schedule.map(describeStep).join(', then ')}, then done`
  }

  const [first, ...rest] = schedule
  if (!rest.length) return `${describeStep(first)}, then every ${describeRepeat(last)}`

  const middle = rest.slice(0, -1)
  const parts = [describeStep(first), ...middle.map(describeStep)]

  return `${parts.join(', then ')}, then every ${describeRepeat(last)}`
}

function describeRepeat(step: IntervalStep): string {
  const unit = UNIT_LABELS[step.unit]
  return step.amount === 1 ? unit : `${step.amount} ${unit}s`
}

/** Ladder rungs offered when building a schedule, and for snoozing. */
export const commonIntervals: IntervalStep[] = [
  { amount: 1, unit: 'day' },
  { amount: 3, unit: 'day' },
  { amount: 1, unit: 'week' },
  { amount: 2, unit: 'week' },
  { amount: 1, unit: 'month' },
  { amount: 3, unit: 'month' },
  { amount: 6, unit: 'month' },
  { amount: 1, unit: 'year' },
]
